// 3.9s — Sentry client-side error tracking.
//
// Goal: every unhandled exception on a vaquero / admin device lands in the
// 'manga-web' Sentry project with enough context to triage without a
// screenshot. The r.bg crash we ate this week (Toast tone palette returned
// undefined, blew up VaqueroShell on AdminShell → Vista de Campo flip)
// should have been a 30-second triage, not a 30-minute one.
//
// Conventions:
//   - DSN lives in import.meta.env.VITE_SENTRY_DSN. Set on Railway only.
//     If unset (e.g. local dev), initSentry() is a no-op — we don't want
//     dev noise polluting the production error feed.
//   - User context is set via setSentryUser() — called from the auth store
//     on login and cleared on logout. Without this, every event would
//     attribute to 'anonymous' and we'd lose role/ranch triage signal.
//   - Sample rate: 1.0 for errors (we want all of them) and 0 for
//     performance traces (we explicitly didn't enable Tracing in the
//     Sentry project setup — keep this off so we don't bleed the free
//     quota).
//   - release tag is wired from import.meta.env.VITE_RELEASE which we
//     set in the Railway build step to the git commit SHA. Lets us
//     correlate 'this error spiked after deploy X' visually in Sentry.

import * as Sentry from '@sentry/react';

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    console.info('[sentry] disabled (no VITE_SENTRY_DSN)');
    return;
  }

  Sentry.init({
    dsn,
    // Release = git SHA injected at build time. Railway build step:
    //   VITE_RELEASE=$(git rev-parse --short HEAD) npm run build
    // Falls back to 'dev' if unset so we don't 400 on Sentry's release
    // field validation.
    release: (import.meta.env.VITE_RELEASE as string) || 'dev',
    environment: (import.meta.env.MODE as string) || 'production',

    // Errors: capture everything. 5K/mo free tier is enormous for our
    // current user base (~10 active users). If we ever blow the quota
    // it'll be because of a real bug spamming, which is the signal we
    // want anyway.
    sampleRate: 1.0,

    // Performance tracing: OFF. We didn't enable the Tracing product
    // in Sentry and there's no point shipping span data Sentry will
    // reject.
    tracesSampleRate: 0,

    // Don't ship PII automatically. We set user context manually via
    // setSentryUser() with the minimum needed (id + role + ranch).
    // No names, no IPs, no PIN values.
    sendDefaultPii: false,

    // Strip noisy 'ResizeObserver loop limit exceeded' and similar
    // browser quirks that aren't actionable. Add more here if Sentry
    // starts filling with junk.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured', // raw Promise.reject('foo') from third-party SDKs
    ],

    // beforeSend: last-mile filter. Useful if we ever need to scrub
    // a specific endpoint's payload from breadcrumbs. No-op for now.
    beforeSend(event) {
      return event;
    },
  });

  initialized = true;
  // build-stamp: 3.9ab-rc1 (force rebuild to bake VITE_SENTRY_DSN)
  console.info('[sentry] initialized, release=', (import.meta.env.VITE_RELEASE as string) || 'dev');
}

/**
 * Attach user context to all subsequent Sentry events. Call from
 * useAuthStore.setAuth on successful login.
 */
export function setSentryUser(userId: string | null, role: string, ranchId = 'HLL-001'): void {
  if (!initialized) return;
  if (!userId) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: userId, role, ranch_id: ranchId });
}

/**
 * Clear user context. Call from useAuthStore.logout.
 */
export function clearSentryUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
}

/**
 * Manual capture — for branches where we *handle* an error but still want
 * Sentry to see it (e.g. an outbox-sender retry that finally gave up).
 */
export function captureSentry(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
      Sentry.captureException(err);
    });
  } else {
    Sentry.captureException(err);
  }
}

// 3.9s — dev helper. Exposes window.__crashSentry() so you can verify
// the integration end-to-end without hunting for a real crash. Only
// attached when Sentry is initialized — won't leak into dev builds.
if (typeof window !== 'undefined') {
  (window as unknown as { __crashSentry?: () => void }).__crashSentry = () => {
    throw new Error('Sentry test crash — if you see this in the dashboard, it works.');
  };
}
